import { mix, rgba } from 'polished'
import {
  AbsoluteColor,
  Color,
  ColorPaletteDefinition,
  ColorShade,
  Opacity,
  Palette,
  ColorPalette,
  PaletteDefinition,
  BaseColor,
  BaseColorPalette,
  BaseColorPaletteDefinition,
} from './palette.types'

export interface PaletteComponentProps {
  theme: {
    palette: Palette
  }
}

/**
 * Makes complete color from potentially uncompleted definition
 */
function repairColor(definition: ColorPaletteDefinition, absMin: string, absMax: string): ColorPalette {
  const color = definition[ColorShade.color]

  return {
    [ColorShade.foreground]: definition[ColorShade.foreground] || absMax,
    [ColorShade.softest]: definition[ColorShade.softest] || mix(0.5, color, absMax),
    [ColorShade.softer]: definition[ColorShade.softer] || mix(0.7, color, absMax),
    [ColorShade.soft]: definition[ColorShade.soft] || mix(0.8, color, absMax),
    [ColorShade.semiSoft]: definition[ColorShade.semiSoft] || mix(0.9, color, absMax),
    [ColorShade.color]: color,
    [ColorShade.semiStrong]: definition[ColorShade.semiStrong] || mix(0.9, color, absMin),
    [ColorShade.strong]: definition[ColorShade.strong] || mix(0.8, color, absMin),
    [ColorShade.stronger]: definition[ColorShade.stronger] || mix(0.7, color, absMin),
    [ColorShade.strongest]: definition[ColorShade.strongest] || mix(0.5, color, absMin),
  }
}

/**
 * Makes complete base color from potentially uncompleted definition
 */
function repairBaseColor(base: BaseColorPaletteDefinition, absMin: string, absMax: string): BaseColorPalette {
  const {
    [BaseColor.shade_0]: shadeMin = absMin,
    [BaseColor.shade_100]: shadeMax = absMax,
    [BaseColor.shade_10]: shadeBase = mix(0.05, shadeMin, shadeMax),
    [BaseColor.shade_20]: shade_20 = mix(0.97, shadeBase, shadeMax),
  } = base

  return {
    [BaseColor.min]: base.min || absMin,
    [BaseColor.shade_0]: shadeMin,
    [BaseColor.shade_10]: shadeBase,
    [BaseColor.shade_15]: base.shade_15 || mix(0.5, shadeBase, shade_20),
    [BaseColor.shade_20]: shade_20,
    [BaseColor.shade_30]: base.shade_30 || mix(0.2, shadeMax, shadeMin),
    [BaseColor.shade_40]: base.shade_40 || mix(0.35, shadeMax, shadeMin),
    [BaseColor.shade_50]: base.shade_50 || mix(0.5, shadeMax, shadeMin),
    [BaseColor.shade_60]: base.shade_60 || mix(0.6, shadeMax, shadeMin),
    [BaseColor.shade_70]: base.shade_70 || mix(0.55, shadeMax, shadeMin),
    [BaseColor.shade_80]: base.shade_80 || mix(0.9, shadeMax, shadeMin),
    [BaseColor.shade_90]: base.shade_90 || mix(0.95, shadeMax, shadeMin),
    [BaseColor.shade_100]: shadeMax,
    [BaseColor.max]: base.max || absMax,
    [BaseColor.surfaceBg]: base.surfaceBg || shadeBase,
    [BaseColor.overlay]: base.overlay || shadeMin,
  }
}

/**
 * Creates theme palette from potentially uncompleted definition
 */
export function repairPalette(definition: PaletteDefinition): Palette {
  const { absolute } = definition
  const { [AbsoluteColor.min]: min, [AbsoluteColor.max]: max } = absolute

  return {
    absolute,
    base: repairBaseColor(definition.base, min, max),
    primary: repairColor(definition.primary, min, max),
    danger: repairColor(definition.danger, min, max),
    warning: repairColor(definition.warning, min, max),
    success: repairColor(definition.success, min, max),
    info: repairColor(definition.info, min, max),
  }
}

export function isColor(color: Color | BaseColor | AbsoluteColor): color is Color {
  return Object.values<string>(Color).includes(color as string)
}

export function isBaseColor(color: Color | BaseColor | AbsoluteColor): color is BaseColor {
  return Object.values<string>(BaseColor).includes(color as string)
}

export function isAbsoluteColor(color: Color | BaseColor | AbsoluteColor): color is AbsoluteColor {
  return Object.values<string>(AbsoluteColor).includes(color as string)
}

const themeColorVariant =
  (color: Color, shade = ColorShade.color) =>
  (props: PaletteComponentProps) =>
    props.theme.palette[color]?.[shade]

const themeAbsoluteColor = (color: AbsoluteColor) => (props: PaletteComponentProps) => props.theme.palette.absolute[color]

const themeBaseColor = (shade: BaseColor) => (props: PaletteComponentProps) => props.theme.palette.base[shade]

export type ThemeColorGetter = (props: PaletteComponentProps) => string
export function themeColor(color: Color, shade?: ColorShade): ThemeColorGetter
export function themeColor(color: BaseColor): ThemeColorGetter
export function themeColor(color: AbsoluteColor): ThemeColorGetter

/**
 * Primary function to extract color from theme
 */
export function themeColor(color: Color | BaseColor | AbsoluteColor, shade = ColorShade.color) {
  return (props: PaletteComponentProps) => {
    if (isColor(color)) {
      return themeColorVariant(color, shade)(props)
    }
    if (isBaseColor(color)) {
      return themeBaseColor(color)(props)
    }
    if (isAbsoluteColor(color)) {
      return themeAbsoluteColor(color)(props)
    }
    return undefined
  }
}

/**
 * Generates transparency color
 */
export const opaqueColor =
  (color: string | ThemeColorGetter, opacity: Opacity = 50) =>
  (props: PaletteComponentProps) => {
    if (typeof color === 'function') {
      return rgba(color(props), opacity / 100)
    }
    return rgba(color, opacity / 100)
  }
